// frontend/src/components/account/MyAccount.jsx
import React, { useState, useEffect } from "react";
import "../../assets/styles/common.css";
import "./MyAccount.css"; // Dedicated CSS for MyAccount

// const API_URL = process.env.REACT_APP_BACKEND_API_URL;
const { API_URL } = require("../../config");
// const API_URL = "/pharmaapi";

const MyAccount = ({ loggedInUser, onUserUpdate }) => {
  // State for password change form
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  // State for profile details form (editable fields) - now initialized with loggedInUser
  const [profileFormData, setProfileFormData] = useState({
    username: loggedInUser?.username || "",
    email: loggedInUser?.email || "",
    fullName: loggedInUser?.fullName || "",
    address: loggedInUser?.address || "",
  });

  // Separate messages and loading states for each form
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordMessageType, setPasswordMessageType] = useState(""); // 'success' or 'error'
  const [loadingPasswordChange, setLoadingPasswordChange] = useState(false);

  const [profileMessage, setProfileMessage] = useState(null);
  const [profileMessageType, setProfileMessageType] = useState(""); // 'success' or 'error'
  const [loadingProfileUpdate, setLoadingProfileUpdate] = useState(false);

  // Initialize form data when component mounts or loggedInUser changes
  useEffect(() => {
    setPasswordFormData({
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    });
    // Set profile form data from loggedInUser
    setProfileFormData({
      username: loggedInUser?.username || "",
      email: loggedInUser?.email || "",
      fullName: loggedInUser?.fullName || "",
      address: loggedInUser?.address || "",
    });
    setPasswordMessage(null);
    setPasswordMessageType("");
    setProfileMessage(null);
    setProfileMessageType("");
  }, [loggedInUser]);

  // Handle change for password form
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle change for profile form
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage(null); // Clear previous messages
    setPasswordMessageType("");
    setLoadingPasswordChange(true);

    const { currentPassword, newPassword, confirmNewPassword } =
      passwordFormData;

    // Client-side validation
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordMessage("Please fill in all password fields.");
      setPasswordMessageType("error");
      setLoadingPasswordChange(false);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordMessage("New password and confirm new password do not match.");
      setPasswordMessageType("error");
      setLoadingPasswordChange(false);
      return;
    }

    if (newPassword.length < 6) {
      setPasswordMessage("New password must be at least 6 characters long.");
      setPasswordMessageType("error");
      setLoadingPasswordChange(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/users/${loggedInUser.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password: newPassword,
          currentPassword: currentPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordMessage("Password changed successfully!");
        setPasswordMessageType("success");
        setPasswordFormData({
          currentPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        });
      } else {
        setPasswordMessage(data.message || "Failed to change password.");
        setPasswordMessageType("error");
      }
    } catch (err) {
      console.error("Error changing password:", err);
      setPasswordMessage("Network error or server unavailable.");
      setPasswordMessageType("error");
    } finally {
      setLoadingPasswordChange(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileMessage(null); // Clear previous messages
    setProfileMessageType("");
    setLoadingProfileUpdate(true);

    const { username, email, fullName, address } = profileFormData;

    // Basic client-side validation
    if (!username || !email) {
      setProfileMessage("Username and Email are required.");
      setProfileMessageType("error");
      setLoadingProfileUpdate(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/users/${loggedInUser.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          email,
          fullName,
          address,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setProfileMessage("Profile updated successfully!");
        setProfileMessageType("success");
        if (onUserUpdate && data.user) {
          onUserUpdate(data.user);
        }
      } else {
        setProfileMessage(data.message || "Failed to update profile.");
        setProfileMessageType("error");
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      setProfileMessage("Network error or server unavailable.");
      setProfileMessageType("error");
    } finally {
      setLoadingProfileUpdate(false);
    }
  };

  if (!loggedInUser) {
    return (
      <div className="my-account-container">
        <p className="fallback-message">
          Please log in to view your account details.
        </p>
      </div>
    );
  }

  return (
    <div className="my-account-container">
      <h2 className="page-title">My Account</h2>

      {/* Combined Profile Details Card (Editable Form with initial values) */}
      <div className="settings-content-section">
        <h3 className="section-title">Manage Your Profile</h3>
        <form onSubmit={handleUpdateProfile} className="settings-form">
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              Username:
            </label>
            <input
              type="text"
              id="username"
              name="username"
              className="form-input"
              value={profileFormData.username}
              onChange={handleProfileChange}
              required
              disabled={loadingProfileUpdate}
            />
          </div>
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email:
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-input"
              value={profileFormData.email}
              onChange={handleProfileChange}
              required
              disabled={loadingProfileUpdate}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fullName" className="form-label">
              Full Name:
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              className="form-input"
              value={profileFormData.fullName}
              onChange={handleProfileChange}
              disabled={loadingProfileUpdate}
            />
          </div>
          <div className="form-group">
            <label htmlFor="address" className="form-label">
              Address:
            </label>
            <input
              type="text"
              id="address"
              name="address"
              className="form-input"
              value={profileFormData.address}
              onChange={handleProfileChange}
              disabled={loadingProfileUpdate}
            />
          </div>
          {/* Display role as non-editable text */}
          <div className="form-group">
            <label className="form-label">Role:</label>
            <p className="form-display-value">{loggedInUser.role}</p>
          </div>

          {profileMessage && (
            <p className={`status-message ${profileMessageType}`}>
              {profileMessage}
            </p>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={loadingProfileUpdate}
            >
              {loadingProfileUpdate
                ? "Updating Profile..."
                : "Save Profile Changes"}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password Card */}
      <div className="settings-content-section">
        <h3 className="section-title">Change Your Password</h3>
        <form onSubmit={handleChangePassword} className="settings-form">
          <div className="form-group">
            <label htmlFor="currentPassword" className="form-label">
              Current Password:
            </label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              className="form-input"
              value={passwordFormData.currentPassword}
              onChange={handlePasswordChange}
              required
              disabled={loadingPasswordChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">
              New Password:
            </label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              className="form-input"
              value={passwordFormData.newPassword}
              onChange={handlePasswordChange}
              required
              disabled={loadingPasswordChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmNewPassword" className="form-label">
              Confirm New Password:
            </label>
            <input
              type="password"
              id="confirmNewPassword"
              name="confirmNewPassword"
              className="form-input"
              value={passwordFormData.confirmNewPassword}
              onChange={handlePasswordChange}
              required
              disabled={loadingPasswordChange}
            />
          </div>

          {passwordMessage && (
            <p className={`status-message ${passwordMessageType}`}>
              {passwordMessage}
            </p>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={loadingPasswordChange}
            >
              {loadingPasswordChange
                ? "Changing Password..."
                : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MyAccount;
